import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useIntl, FormattedMessage } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import {
  makeStyles,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
} from '@material-ui/core';
import { Element } from 'react-scroll';
import InfiniteScroll from 'react-infinite-scroller';
import startCase from 'lodash/startCase';
import CircularProgress from 'components/shared/CircularProgress';
import { getColorFromLetter } from 'logic/colors';
import moment from 'logic/moment';
import PATHS from 'constants/paths';
import Message from './Messages/Message';

const styles = makeStyles((theme) => ({
  chipWrap: {
    textAlign: 'right',
  },
  chip: {
    margin: theme.spacing(0.25),
  },
  listItem: {
    padding: theme.spacing(1, 2),
  },
  text: {
    marginLeft: theme.spacing(2),
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  },
}));

// The list endpoint doesn't expose `is_deleted`, so a deleted user is detected
// by their scrubbed `name` ("Deleted"). Shown as "Borttagen användare" to match
// the conversation view (DeletedUser), not the bare "Deleted"/"Borttagen".
const isDeletedName = (name) => name === 'Deleted';
const deletedUserLabel = (intl) =>
  intl.formatMessage({ id: 'DeletedUser', defaultMessage: 'Deleted user' });

export const getCommaSeparatedUsernames = (otherUsers, intl) => {
  const names = otherUsers.map((user) =>
    isDeletedName(user.name) ? deletedUserLabel(intl) : user.name,
  );

  return names.join(', ');
};

const getShortText = (msg) => {
  const length = 240;
  const { text } = msg;

  if (!text) {
    return msg;
  }

  const shortText = text.replace(/(?:\r\n|\r|\n)/g, ' ').substring(0, length);

  return {
    ...msg,
    text: shortText.length < length ? shortText : `${shortText}...`,
  };
};

export const getItemInfo = (item, currentUser, intl) => {
  const otherUser = item.Users.filter(({ id }) => id !== currentUser.id);
  const nameToColor = otherUser[0] ? otherUser[0].name : currentUser.name;
  const color = getColorFromLetter(nameToColor[0]);
  const msg = item.Messages[0] ? getShortText(item.Messages[0]) : null;

  let userName = item.Users.find(({ id }) => id === msg?.user_id)?.name || '';

  if (isDeletedName(userName)) {
    userName = deletedUserLabel(intl);
  }

  let iconName;
  if (otherUser[0] && (otherUser[0].name || otherUser[0].username)) {
    iconName = otherUser[0].name || otherUser[0].username;
  } else {
    iconName = currentUser.name || currentUser.username;
  }

  // Match the conversation view: a deleted counterpart shows an "X" avatar.
  const icon = isDeletedName(otherUser[0]?.name) ? 'X' : startCase(iconName).charAt(0);

  return {
    styleForIcon: { backgroundColor: color },
    icon,
    usersCommaSeparated: getCommaSeparatedUsernames(item.Users, intl),
    // Counterpart name(s) only (excludes current user), matching the
    // conversation view's AppBar title -- passed via Link state so the title
    // shows immediately instead of popping in once the conversation loads.
    counterpartName: getCommaSeparatedUsernames(otherUser, intl),
    messageString: msg ? (
      <Message
        addYouPrefix={msg.user_id === currentUser.id}
        hideLink
        isMy={msg.user_id === currentUser.id}
        isUnRead={!!item.unread_messages}
        msg={msg}
        userName={userName}
      />
    ) : null,
    date: msg?.created_at ? moment(msg.created_at).format('LL') : '',
  };
};

const ConversationsList = ({
  hasMore,
  fetchMoreData,
  data,
  currentUser,
  previousConversationId,
  isLoading,
}) => {
  const classes = styles();
  const intl = useIntl();
  const empty = !isLoading && isEmpty(data);

  return (
    <List>
      <InfiniteScroll
        initialLoad={false}
        pageStart={0}
        loadMore={fetchMoreData}
        hasMore={hasMore}
        loader={<CircularProgress key="loader" />}
      >
        {empty && (
          <Box p={2}>
            <Typography>
              <FormattedMessage
                id="conversations.emptyMessages"
                defaultMessage="You don't have any conversations yet,
                  to contact a host use the contact form in the ad on the main site. {clickHere}"
                values={{
                  clickHere: (
                    <a href="/resultatnysokning.asp">
                      <FormattedMessage
                        id="conversations.emptyMessagesClick"
                        defaultMessage="Click here to search for cottages"
                      />
                    </a>
                  ),
                }}
              />
            </Typography>
          </Box>
        )}

        {data.map((item) => {
          const info = getItemInfo(item, currentUser, intl);

          return (
            <ListItem
              divider
              key={item.id}
              component={Link}
              to={{
                pathname: PATHS.conversations.read.replace(':conversationId', item.id),
                state: { counterpartName: info.counterpartName },
              }}
              className={classes.listItem}
            >
              {item.id === previousConversationId && (
                <Element name="previousConversationId" style={{ position: 'absolute', top: 0 }} />
              )}
              <Avatar style={info.styleForIcon}>{info.icon}</Avatar>
              <ListItemText
                primary={info.messageString}
                secondary={[info.usersCommaSeparated, info.date].filter(Boolean).join(' - ')}
                className={classes.text}
              />
              <div className={classes.chipWrap}>
                {item.tags.map(({ id, tag }) => (
                  <Chip key={id} label={tag} className={classes.chip} />
                ))}
              </div>
            </ListItem>
          );
        })}
      </InfiniteScroll>
    </List>
  );
};

ConversationsList.propTypes = {
  hasMore: PropTypes.bool,
  fetchMoreData: PropTypes.func.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({})),
  currentUser: PropTypes.shape({}),
  previousConversationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isLoading: PropTypes.bool,
};

export default ConversationsList;
